import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CalendarCheck, PawPrint } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BookingForm } from './BookingForm';

export const BookingPopup = () => {
  const [showInitialPopup, setShowInitialPopup] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    // Check if user has dismissed the popup before
    const hasSeenPopup = sessionStorage.getItem('petGuardioesPopupSeen');
    
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setShowInitialPopup(true);
      }, 5000); // Show after 5 seconds

      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem('petGuardioesPopupSeen', 'true');
    setShowInitialPopup(false);
  };

  const handleBookNow = () => {
    sessionStorage.setItem('petGuardioesPopupSeen', 'true');
    setShowInitialPopup(false);
    
    // Scroll to booking section instead of modal
    setTimeout(() => {
      const element = document.querySelector('#agendamento');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <>
      {/* Initial Popup */}
      <AnimatePresence>
        {showInitialPopup && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="booking-popup"
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleDismiss}
            />

            {/* Popup Content */}
            <motion.div
              className="relative bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              {/* Close button */}
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Fechar"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>

              {/* Icon */}
              <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                >
                  <PawPrint className="w-8 h-8 text-yellow-600" />
                </motion.div>
              </div>

              {/* Text */}
              <h3 className="font-nunito text-2xl font-bold text-gray-900 text-center mb-2">
                Quer marcar o banho ou a tosquia do seu patudo?
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Agende agora de forma rápida e fácil pelo nosso formulário.
              </p>

              {/* Decorative paws */}
              <div className="flex justify-center gap-2 mb-6">
                {[1, 2, 3].map((i) => (
                  <motion.span
                    key={i}
                    className="text-2xl"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity, repeatDelay: 1 }}
                  >
                    🐾
                  </motion.span>
                ))}
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleBookNow}
                  className="w-full h-14 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-lg rounded-full shadow-lg shadow-yellow-500/30"
                  data-testid="popup-book-now-btn"
                >
                  <CalendarCheck className="w-5 h-5 mr-2" />
                  Agendar agora
                </Button>
                <button
                  onClick={handleDismiss}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium py-2"
                >
                  Talvez mais tarde
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-nunito text-2xl font-bold">
              Agendar Serviço
            </DialogTitle>
            <DialogDescription>
              Preencha os dados para marcar o serviço do seu patudo.
            </DialogDescription>
          </DialogHeader>
          <BookingForm isModal onClose={() => setShowBookingModal(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};
